import React, { useMemo, useState } from "react";
import { Users, Search, MoreVertical, AtSign } from "lucide-react";
import { EmptyState } from "../EmptyState/index.tsx";
import { useFriendsList } from "../../hooks/useFriendsList.tsx";
import Input from "../ui/input.tsx";

const FriendsList = () => {
  const { friendsList } = useFriendsList();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return friendsList;
    return friendsList.filter((f) =>
      [f.first_name, f.email, f.user_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [friendsList, query]);

  return (
    <div className="w-full">
      <div className="card-glass p-5 border border-white/10">
        {/* Top bar: Search and Count */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="relative w-full sm:max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
            <Input
              placeholder="Search connections by name, email or @username"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="text-sm text-white/70 ml-auto">
            <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10">{filtered.length}</span>
            <span className="ml-2">connection{filtered.length === 1 ? "" : "s"}</span>
          </div>
        </div>

        {/* List / Empty state */}
        {friendsList.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No connections yet — add someone to start splitting."
            actionLabel="+ Add Connection"
            onAction={() => {}}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((friend) => {
              const initial = (friend.first_name || friend.email || "").charAt(0).toUpperCase();
              return (
                <div
                  key={friend.id}
                  className="group relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4 transition shadow hover:shadow-lg hover:border-white/20"
                >
                  <div className="flex items-start gap-3">
                    <div className="relative h-12 w-12 shrink-0">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-500/30 to-cyan-400/30 blur"></div>
                      <div className="relative h-12 w-12 rounded-full bg-slate-800/80 flex items-center justify-center text-white font-semibold">
                        {initial}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-semibold truncate">{friend.first_name}</p>
                        {friend.user_name && (
                          <span className="inline-flex items-center gap-1 text-xs text-white/70 border border-white/15 rounded-md px-2 py-0.5">
                            <AtSign size={12} />{friend.user_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white/70 truncate">{friend.email}</p>
                    </div>
                    <button
                      className="ml-auto opacity-0 group-hover:opacity-100 transition text-white/60 hover:text-white"
                      title="More"
                    >
                      <MoreVertical size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsList;
